import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useReducer,
	useRef,
} from "react";
import { debounce } from "lodash";
import type { AppAction } from "@/app/state/actions";
import type { AppState } from "@/app/state/types";
import { appReducer } from "@/app/state/reducer";
import { createInitialState } from "@/app/state/state";
import type { LiveQuerySession } from "@/features/chats/lib/conversationSession";
import { getAppAccessToken, refreshAppAccessToken } from "@/shared/data/appAuth";
import { setAccessToken } from "@/shared/data";
import { isAppMode } from "@/shared/utils/routing";
import { syncThemeMode } from "@/shared/styles/theme";
import { writeStoredTransportMode } from "@/features/transport/lib/transportMode";
import { setTransportModeProvider } from "@/shared/data";

export interface AppContextValue {
	state: AppState;
	dispatch: React.Dispatch<AppAction>;
	stateRef: React.MutableRefObject<AppState>;
	querySessionsRef: React.MutableRefObject<Map<string, LiveQuerySession>>;
	chatQuerySessionIndexRef: React.MutableRefObject<Map<string, string>>;
	activeQuerySessionRequestIdRef: React.MutableRefObject<string>;
}

const AppContext = createContext<AppContextValue | null>(null);

export function applyActionToStateRef(
	stateRef: React.MutableRefObject<AppState>,
	action: AppAction,
): void {
	stateRef.current = appReducer(stateRef.current, action);
}

export function syncTransportModeProvider(
	stateRef: React.MutableRefObject<AppState>,
): void {
	setTransportModeProvider(() => stateRef.current.transportMode);
}

export function syncApiAccessToken(state: AppState): void {
	setAccessToken(state.accessToken);
}

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({
	children,
}) => {
	const [state, baseDispatch] = useReducer(
		appReducer,
		undefined,
		createInitialState,
	);
	const stateRef = useRef(state);
	const querySessionsRef = useRef(new Map<string, LiveQuerySession>());
	const chatQuerySessionIndexRef = useRef(new Map<string, string>());
	const activeQuerySessionRequestIdRef = useRef("");
	stateRef.current = state;

	const debouncedSetStreamingRef = useRef(
		debounce(
			(action: Extract<AppAction, { type: "SET_STREAMING" }>) => {
				baseDispatch(action);
			},
			300,
		),
	);

	useEffect(() => {
		const debounced = debouncedSetStreamingRef.current;
		return () => {
			debounced.cancel();
		};
	}, []);

	const dispatch = useCallback<React.Dispatch<AppAction>>((action) => {
		if (
			action.type === "SHOW_COMMAND_STATUS_OVERLAY" ||
			action.type === "HIDE_COMMAND_STATUS_OVERLAY" ||
			action.type === "RESET_CONVERSATION" ||
			action.type === "RESET_ACTIVE_CONVERSATION"
		) {
			const overlayTimer = stateRef.current.commandStatusOverlay.timer;
			if (overlayTimer) {
				clearTimeout(overlayTimer);
			}
		}
		if (
			action.type === "RESET_CONVERSATION" ||
			action.type === "RESET_ACTIVE_CONVERSATION"
		) {
			debouncedSetStreamingRef.current.cancel();
			const artifactTimer = stateRef.current.artifactAutoCollapseTimer;
			if (artifactTimer) {
				clearTimeout(artifactTimer);
			}
			const planTimer = stateRef.current.planAutoCollapseTimer;
			if (planTimer) {
				clearTimeout(planTimer);
			}
			for (const timer of stateRef.current.reasoningCollapseTimers.values()) {
				clearTimeout(timer);
			}
		}
		if (action.type === "SET_STREAMING") {
			applyActionToStateRef(stateRef, action);
			debouncedSetStreamingRef.current(action);
			return;
		}
		applyActionToStateRef(stateRef, action);
		baseDispatch(action);
	}, []);

	const value = useMemo<AppContextValue>(
		() => ({
			state,
			dispatch,
			stateRef,
			querySessionsRef,
			chatQuerySessionIndexRef,
			activeQuerySessionRequestIdRef,
		}),
		[state, dispatch],
	);

	useEffect(() => {
		syncThemeMode(state.themeMode);
	}, [state.themeMode]);

	useEffect(() => {
		writeStoredTransportMode(state.transportMode);
	}, [state.transportMode]);

	useEffect(() => {
		syncApiAccessToken(state);
	}, [state.accessToken]);

	useEffect(() => {
		if (!isAppMode()) {
			return;
		}

		let cancelled = false;
		const currentToken = getAppAccessToken() || "";
		if (currentToken) {
			setAccessToken(currentToken);
			if (currentToken !== stateRef.current.accessToken) {
				dispatch({ type: "SET_ACCESS_TOKEN", token: currentToken });
			}
			return;
		}

		refreshAppAccessToken("missing")
			.then((token) => {
				if (cancelled || !token) {
					return;
				}
				setAccessToken(token);
				if (token !== stateRef.current.accessToken) {
					dispatch({ type: "SET_ACCESS_TOKEN", token });
				}
			})
			.catch(() => undefined);

		return () => {
			cancelled = true;
		};
	}, [dispatch]);

	return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export function useAppContext(): AppContextValue {
	const ctx = useContext(AppContext);
	if (!ctx) {
		throw new Error("useAppContext must be used within an AppProvider");
	}
	return ctx;
}

export function useOptionalAppContext(): AppContextValue | null {
	return useContext(AppContext);
}

export function useAppState(): AppState {
	return useAppContext().state;
}

export function useAppDispatch(): React.Dispatch<AppAction> {
	return useAppContext().dispatch;
}
